import { type Brand, make } from "ts-brand";
import { z } from "zod";

import { type DocumentId, refineUploadFile, UploadDocumentInputObjectSchema } from "./document";
import { MandateId } from "./mandate";
import { PageRequestSchema } from "./pagination";
// Type-only (erased at runtime) so the runtime import graph stays acyclic even
// though task-schedule.ts depends on this module — mirrors the `type DocumentId`
// import above. The provenance link points a generated list back at its plan.
import type { TaskScheduleId } from "./task-schedule";

/**
 * TaskList / TaskItem domain model (Core 2 — S5 Follow-up). A TaskList is a
 * temporary dispatch bundle of open TaskItems that Trexpert sends to a customer.
 * Two TaskItem kinds: ReceiptTaskItem (structured: date, description, amount —
 * closed by an uploaded Document) and FreeformTaskItem (unstructured: name,
 * description — closed by any file). Either closes with a Remark when no file
 * can be supplied.
 * Closure is final (one-way). Lifecycle: drafted → sent → closed (manual by
 * Trexpert). After sent, items stay editable; closed items are final.
 * See docs/architecture.md § "Core 2 — Follow-up" and
 * docs/stories/dscvr-nachfass-aufgaben for the refined invariants I1–I4.
 */

// --- Branded IDs ---

export type TaskListId = Brand<string, "TaskListId">;
export const TaskListId = make<TaskListId>();

export type TaskItemId = Brand<string, "TaskItemId">;
export const TaskItemId = make<TaskItemId>();

// --- Currencies ---

export const CURRENCIES = ["CHF"] as const;
export type Currency = (typeof CURRENCIES)[number];
export const CurrencySchema = z.enum(CURRENCIES);

// --- Statuses ---

export const TASK_ITEM_STATUSES = ["open", "closed"] as const;
export type TaskItemStatus = (typeof TASK_ITEM_STATUSES)[number];
export const TaskItemStatusSchema = z.enum(TASK_ITEM_STATUSES);

export const TASK_LIST_STATUSES = ["drafted", "sent", "submitted", "closed"] as const;
export type TaskListStatus = (typeof TASK_LIST_STATUSES)[number];
export const TaskListStatusSchema = z.enum(TASK_LIST_STATUSES);

/**
 * Lifecycle rank of a TaskList status — its index in {@link TASK_LIST_STATUSES}
 * (drafted < sent < submitted < closed). The single source the UI sorts by. «Wer
 * ist am Zug» is a filter concern, not a re-ordering of this lifecycle.
 */
export const statusLifecycleRank = (status: TaskListStatus): number => TASK_LIST_STATUSES.indexOf(status);

/**
 * Status buckets the cross-mandate overview filters by (the German tab labels).
 * Each bucket maps to exactly one TaskList status — the former «offen» bucket
 * (which lumped `sent` + `submitted` together) is split into separate «gesendet»
 * and «eingereicht» tabs so a submitted list is never shown as «offen».
 */
export const TASK_LIST_STATUS_FILTERS = [
    "alle",
    "entwurf",
    "gesendet",
    "eingereicht",
    "abgeschlossen",
] as const;
export type TaskListStatusFilter = (typeof TASK_LIST_STATUS_FILTERS)[number];

/**
 * Whether a TaskList status falls in the chosen overview filter bucket. Shared by
 * the client tabs and the server query so both sides agree on what each tab means.
 * Every non-«alle» bucket targets a single status — one tab per lifecycle state.
 */
export const matchesTaskListStatusFilter = (
    status: TaskListStatus,
    filter: TaskListStatusFilter
): boolean => {
    switch (filter) {
        case "alle":
            return true;
        case "entwurf":
            return status === "drafted";
        case "gesendet":
            return status === "sent";
        case "eingereicht":
            return status === "submitted";
        case "abgeschlossen":
            return status === "closed";
    }
};

// --- Remark ---

/** Free text a customer records to close a TaskItem when no file is available. */
export const RemarkSchema = z.object({ text: z.string().min(1) });
export type Remark = z.infer<typeof RemarkSchema>;

// --- TaskItem kinds ---

/**
 * Structured TaskItem: requires a concrete document (date, description, amount).
 * Closed by the linked (inbound) Document's DocumentId, or by a Remark.
 */
export const ReceiptTaskItemSchema = z.object({
    id: z.string().min(1).transform(TaskItemId),
    kind: z.literal("receipt"),
    date: z.string().min(1),
    description: z.string().min(1),
    amount: z.string().min(1),
    currency: CurrencySchema.default("CHF"),
    status: TaskItemStatusSchema,
    closedByDocumentId: z
        .string()
        .min(1)
        .transform(v => v as DocumentId)
        .optional(),
    remark: RemarkSchema.optional(),
    updatedAt: z.string().optional(),
});
export type ReceiptTaskItem = z.infer<typeof ReceiptTaskItemSchema>;

/**
 * Freeform TaskItem: free-form request (name, description). Closed by any
 * uploaded file (stored as an inbound Document) or by a Remark.
 */
export const FreeformTaskItemSchema = z.object({
    id: z.string().min(1).transform(TaskItemId),
    kind: z.literal("freeform"),
    name: z.string().min(1),
    description: z.string(),
    status: TaskItemStatusSchema,
    closedByDocumentId: z
        .string()
        .min(1)
        .transform(v => v as DocumentId)
        .optional(),
    remark: RemarkSchema.optional(),
    updatedAt: z.string().optional(),
});
export type FreeformTaskItem = z.infer<typeof FreeformTaskItemSchema>;

/** Discriminated union of the two TaskItem kinds. */
export const TaskItemSchema = z.discriminatedUnion("kind", [ReceiptTaskItemSchema, FreeformTaskItemSchema]);
export type TaskItem = z.infer<typeof TaskItemSchema>;

// --- TaskList aggregate ---

export const TaskListSchema = z.object({
    id: z.string().min(1).transform(TaskListId),
    mandateId: z.string().min(1).transform(MandateId),
    title: z.string().min(1),
    status: TaskListStatusSchema,
    items: z.array(TaskItemSchema),
    /**
     * Provenance: set when this list was materialised by the `TaskScheduler` from
     * a recurring Aufgabenplan (its {@link TaskScheduleId}). Absent on one-off
     * lists. Powers the «aus Plan» chip and lets
     * the overview link a generated list back to its plan. Each occurrence is a
     * separate list with its own id — a generated list is never re-opened.
     */
    generatedFromScheduleId: z
        .string()
        .min(1)
        .transform(v => v as TaskScheduleId)
        .optional(),
    createdAt: z.string().min(1),
    updatedAt: z.string().optional(),
    sentAt: z.string().optional(),
    submittedAt: z.string().optional(),
    closedAt: z.string().optional(),
});
export type TaskList = z.infer<typeof TaskListSchema>;

/** How many of a TaskList's items are still open — the customer's outstanding work. */
export const openItemCount = (taskList: Pick<TaskList, "items">): number =>
    taskList.items.filter(item => item.status === "open").length;

/** Mandate-scoped lists are shown newest-first (createdAt descending). */
export const compareTaskListsByCreatedAtDesc = (a: TaskList, b: TaskList): number =>
    b.createdAt.localeCompare(a.createdAt);

export const sortTaskListsByCreatedAtDesc = (taskLists: TaskList[]): TaskList[] =>
    [...taskLists].sort(compareTaskListsByCreatedAtDesc);

// --- Cross-mandate overview (paginated) ---

/**
 * Sortable columns of the cross-mandate task-list overview. Constrained to
 * columns that live on the TaskList record itself — `companyName` (joined in
 * after the query) and the derived progress counts are deferred, mirroring how
 * the document overview defers its enriched company-name column.
 */
export const TASK_LIST_OVERVIEW_SORT_FIELDS = [
    "companyName",
    "title",
    "status",
    "closedCount",
    "sentAt",
    "updatedAt",
] as const;
export type TaskListOverviewSortField = (typeof TASK_LIST_OVERVIEW_SORT_FIELDS)[number];

/**
 * Boundary query for the paginated cross-mandate task-list overview (replaces the
 * old `listAllTaskLists` fetch-everything). `search` matches the list title *and*
 * the Mandate company name (the server resolves the company-name half);
 * `statusFilter` is the «Alle/Entwurf/Gesendet/Eingereicht/Abgeschlossen» tab. `sortBy` covers
 * record-backed columns plus enriched `companyName` and derived `closedCount`
 * (progress) — both resolved in the service/repository layer. With no sort the
 * overview defaults to most-recently-active first.
 */
export const TaskListOverviewQuerySchema = PageRequestSchema.extend({
    sortBy: z.enum(TASK_LIST_OVERVIEW_SORT_FIELDS).optional(),
    statusFilter: z.enum(TASK_LIST_STATUS_FILTERS).optional(),
    /** Restrict the cross-mandate overview to a single Mandate. */
    mandateId: z.string().min(1).transform(MandateId).optional(),
});
export type TaskListOverviewQuery = z.infer<typeof TaskListOverviewQuerySchema>;

/**
 * One row of the cross-mandate task-list overview: a TaskList projected to the
 * columns the overview shows, joined with its Mandate `companyName` and with the
 * item progress pre-counted (so the row never ships the full `items` array).
 */
export interface TaskListOverviewItem {
    id: TaskListId;
    mandateId: MandateId;
    companyName: string;
    title: string;
    status: TaskListStatus;
    itemCount: number;
    closedCount: number;
    sentAt?: string;
    updatedAt?: string;
}

// --- Input schemas ---

export const CreateTaskListInputSchema = z.object({
    mandateId: z.string().min(1).transform(MandateId),
    title: z.string().min(1),
});
export type CreateTaskListInput = z.infer<typeof CreateTaskListInputSchema>;

/**
 * Boundary input for `sendTaskList`. `templateId` is the Nachrichtenvorlage the
 * trustee picked for this Vorgang; absent means «take the Vorauswahl of the
 * occasion». One choice per send — every member of the customer org receives the
 * same text, addressed with their own first name.
 */
export const SendTaskListInputSchema = z.object({
    taskListId: z.string().min(1).transform(TaskListId),
    templateId: z.string().min(1).optional(),
});
export type SendTaskListInput = z.infer<typeof SendTaskListInputSchema>;

export const AddReceiptTaskItemInputSchema = z.object({
    taskListId: z.string().min(1).transform(TaskListId),
    kind: z.literal("receipt"),
    date: z.string().min(1),
    description: z.string().min(1),
    amount: z.string().min(1),
    currency: CurrencySchema.default("CHF"),
});

export const AddFreeformTaskItemInputSchema = z.object({
    taskListId: z.string().min(1).transform(TaskListId),
    kind: z.literal("freeform"),
    name: z.string().min(1),
    description: z.string().default(""),
});

export const AddTaskItemInputSchema = z.discriminatedUnion("kind", [
    AddReceiptTaskItemInputSchema,
    AddFreeformTaskItemInputSchema,
]);
export type AddTaskItemInput = z.infer<typeof AddTaskItemInputSchema>;

export const RemoveTaskItemInputSchema = z.object({
    taskListId: z.string().min(1).transform(TaskListId),
    taskItemId: z.string().min(1).transform(TaskItemId),
});
export type RemoveTaskItemInput = z.infer<typeof RemoveTaskItemInputSchema>;

export const CloseTaskItemWithRemarkInputSchema = z.object({
    taskListId: z.string().min(1).transform(TaskListId),
    taskItemId: z.string().min(1).transform(TaskItemId),
    remarkText: z.string().min(1),
});
export type CloseTaskItemWithRemarkInput = z.infer<typeof CloseTaskItemWithRemarkInputSchema>;

export const UpdateTaskItemRemarkInputSchema = z.object({
    taskListId: z.string().min(1).transform(TaskListId),
    taskItemId: z.string().min(1).transform(TaskItemId),
    remarkText: z.string().min(1),
});
export type UpdateTaskItemRemarkInput = z.infer<typeof UpdateTaskItemRemarkInputSchema>;

const UpdateReceiptTaskItemInputSchema = z.object({
    taskListId: z.string().min(1).transform(TaskListId),
    taskItemId: z.string().min(1).transform(TaskItemId),
    kind: z.literal("receipt"),
    date: z.string().min(1),
    description: z.string().min(1),
    amount: z.string().min(1),
    currency: CurrencySchema.default("CHF"),
});

const UpdateFreeformTaskItemInputSchema = z.object({
    taskListId: z.string().min(1).transform(TaskListId),
    taskItemId: z.string().min(1).transform(TaskItemId),
    kind: z.literal("freeform"),
    name: z.string().min(1),
    description: z.string().default(""),
});

export const UpdateTaskItemInputSchema = z.discriminatedUnion("kind", [
    UpdateReceiptTaskItemInputSchema,
    UpdateFreeformTaskItemInputSchema,
]);
export type UpdateTaskItemInput = z.infer<typeof UpdateTaskItemInputSchema>;

/**
 * Extended input for `uploadDocument` that optionally binds the uploaded file
 * to a TaskItem — closing it as part of the same call. When `replacingDocumentId`
 * is set the old document is deleted and the task item's reference is updated
 * (only valid before the task list is submitted). Added here (not in document.ts)
 * to keep the import direction acyclic: task-list imports document, not the reverse.
 */
export const UploadDocumentWithTaskItemInputSchema = UploadDocumentInputObjectSchema.extend({
    taskListId: z.string().min(1).transform(TaskListId).optional(),
    taskItemId: z.string().min(1).transform(TaskItemId).optional(),
    replacingDocumentId: z
        .string()
        .min(1)
        .transform(v => v as DocumentId)
        .optional(),
}).superRefine(refineUploadFile);
export type UploadDocumentWithTaskItemInput = z.infer<typeof UploadDocumentWithTaskItemInputSchema>;

// --- Domain errors ---

export class TaskListError extends Error {
    constructor(message: string) {
        super(message);
        this.name = "TaskListError";
    }
}

// --- Read-model rules ---

/**
 * The Documents that must stay hidden from the *trustee* until the customer
 * submits: those bound (via `closedByDocumentId`) to a TaskItem of a list that
 * is still `sent` — i.e. the customer has uploaded a file against a task but has
 * not yet submitted the list. Once the list is `submitted` or `closed` the
 * documents become visible. The customer always sees their own uploads; this
 * rule only defers the trustee's view. Pure so the listing service applies it
 * without re-deriving the policy inline.
 */
export const pendingTaskItemDocumentIds = (lists: ReadonlyArray<TaskList>): Set<DocumentId> => {
    const pending = new Set<DocumentId>();
    for (const list of lists) {
        if (list.status !== "sent") {
            continue;
        }
        for (const item of list.items) {
            if (item.closedByDocumentId) {
                pending.add(item.closedByDocumentId);
            }
        }
    }
    return pending;
};

// --- Invariant enforcement — pure functions ---

/**
 * I2: add a TaskItem to a TaskList. Items may only be added while the list is
 * `drafted`. The new item starts `open`; its id and timestamp are supplied by
 * the caller (the domain stays free of `crypto`/clock). The `taskListId` on the
 * input is ignored — the target list is the one passed in.
 */
export const applyAddTaskItem = (
    list: TaskList,
    input: AddTaskItemInput,
    id: TaskItemId,
    at: string
): TaskList => {
    if (list.status !== "drafted") {
        throw new TaskListError(
            `Cannot add a TaskItem to a TaskList in status "${list.status}" — only "drafted" is allowed`
        );
    }
    const newItem: TaskItem =
        input.kind === "receipt"
            ? {
                  id,
                  kind: "receipt",
                  date: input.date,
                  description: input.description,
                  amount: input.amount,
                  currency: input.currency,
                  status: "open",
                  updatedAt: at,
              }
            : {
                  id,
                  kind: "freeform",
                  name: input.name,
                  description: input.description,
                  status: "open",
                  updatedAt: at,
              };
    return { ...list, updatedAt: at, items: [...list.items, newItem] };
};

/**
 * I2: remove a TaskItem from a TaskList. Allowed only while the list is
 * `drafted`. Removing an unknown item is a no-op on the item set but still
 * stamps `updatedAt`, mirroring the prior service behaviour.
 */
export const applyRemoveTaskItem = (list: TaskList, taskItemId: TaskItemId, at: string): TaskList => {
    if (list.status !== "drafted") {
        throw new TaskListError(
            `Cannot remove a TaskItem from a TaskList in status "${list.status}" — only "drafted" is allowed`
        );
    }
    return {
        ...list,
        updatedAt: at,
        items: list.items.filter(item => item.id !== taskItemId),
    };
};

/**
 * I1 / I2: drafted → sent. Validates status and non-empty item list.
 * Items stay editable after sent — this function only changes list status.
 */
export const applySendTaskList = (list: TaskList, at: string): TaskList => {
    if (list.status !== "drafted") {
        throw new TaskListError(
            `Cannot send a TaskList in status "${list.status}" — only "drafted" is allowed`
        );
    }
    if (list.items.length === 0) {
        throw new TaskListError("Cannot send a TaskList with zero items");
    }
    return { ...list, status: "sent", sentAt: at, updatedAt: at };
};

/**
 * sent → submitted (customer's explicit act). Documents uploaded for items
 * become visible to the trustee only after this transition. The customer may
 * only submit once every item is cleared — i.e. each TaskItem is "closed" via
 * a Document or a Remark (I3). A list with open items cannot be submitted.
 */
export const applySubmitTaskList = (list: TaskList, at: string): TaskList => {
    if (list.status !== "sent") {
        throw new TaskListError(
            `Cannot submit a TaskList in status "${list.status}" — only "sent" is allowed`
        );
    }
    const openItems = list.items.filter(item => item.status === "open").length;
    if (openItems > 0) {
        throw new TaskListError(
            `Cannot submit a TaskList with ${openItems} open item(s) — all items must be cleared first`
        );
    }
    return { ...list, status: "submitted", submittedAt: at, updatedAt: at };
};

/**
 * I4: sent/submitted → closed (Trexpert's manual act). Open items stay open —
 * this function only changes list status, not item statuses.
 */
export const applyCloseTaskList = (list: TaskList, at: string): TaskList => {
    if (list.status !== "sent" && list.status !== "submitted") {
        throw new TaskListError(
            `Cannot close a TaskList in status "${list.status}" — only "sent" or "submitted" are allowed`
        );
    }
    return { ...list, status: "closed", closedAt: at, updatedAt: at };
};

/**
 * I3: TaskItem closed ⇔ a Document is assigned OR a Remark is recorded; closure
 * is one-way. Validates that the item exists, is open, and that exactly one of
 * documentId / remarkText is provided (not both, not neither).
 */
export const applyCloseTaskItem = (
    list: TaskList,
    taskItemId: TaskItemId,
    opts: { documentId?: DocumentId; remarkText?: string },
    at: string
): TaskList => {
    if (list.status === "drafted") {
        throw new TaskListError("Cannot close a TaskItem on a drafted TaskList — send it first");
    }
    const item = list.items.find(it => it.id === taskItemId);
    if (!item) {
        throw new TaskListError(`TaskItem ${taskItemId} not found in TaskList ${list.id}`);
    }
    if (item.status === "closed") {
        throw new TaskListError(`TaskItem ${taskItemId} is already closed`);
    }
    if (!opts.documentId && !opts.remarkText) {
        throw new TaskListError("Cannot close a TaskItem without a Document or a Remark (I3)");
    }
    const remark = opts.remarkText ? { text: opts.remarkText } : undefined;
    const closedItem: TaskItem = {
        ...item,
        status: "closed",
        closedByDocumentId: opts.documentId,
        remark,
        updatedAt: at,
    };
    return {
        ...list,
        updatedAt: at,
        items: list.items.map(it => (it.id === taskItemId ? closedItem : it)),
    };
};

/**
 * Replace the document linked to a closed task item — valid only while the list
 * is still "sent", i.e. before the customer submits it. Submitting locks the
 * list for the customer: corrections after submit go through a new TaskList, not
 * by editing the submitted one (decision 2026-07-08). The old document is removed
 * externally; this function only updates the reference.
 */
export const applyReplaceTaskItemDocument = (
    list: TaskList,
    taskItemId: TaskItemId,
    newDocumentId: DocumentId,
    at: string
): TaskList => {
    if (list.status !== "sent") {
        throw new TaskListError(`Document replacement only allowed in "sent" status, not "${list.status}"`);
    }
    const item = list.items.find(it => it.id === taskItemId);
    if (!item) {
        throw new TaskListError(`TaskItem ${taskItemId} not found in TaskList ${list.id}`);
    }
    if (item.status !== "closed" || !item.closedByDocumentId) {
        throw new TaskListError(`TaskItem ${taskItemId} must be closed by a document to replace it`);
    }
    const updatedItem: TaskItem = { ...item, closedByDocumentId: newDocumentId, updatedAt: at };
    return {
        ...list,
        updatedAt: at,
        items: list.items.map(it => (it.id === taskItemId ? updatedItem : it)),
    };
};

/**
 * Edit the free text of a Remark a customer recorded to close a TaskItem (I3).
 * Valid only while the list is still "sent", mirroring
 * {@link applyReplaceTaskItemDocument}: submitting locks the list for the
 * customer, and the text becomes final. The item must be closed *by a Remark*
 * (not by a Document); the closure stands, only the text changes.
 */
export const applyUpdateTaskItemRemark = (
    list: TaskList,
    taskItemId: TaskItemId,
    remarkText: string,
    at: string
): TaskList => {
    if (list.status !== "sent") {
        throw new TaskListError(`Remark editing only allowed in "sent" status, not "${list.status}"`);
    }
    const item = list.items.find(it => it.id === taskItemId);
    if (!item) {
        throw new TaskListError(`TaskItem ${taskItemId} not found in TaskList ${list.id}`);
    }
    if (item.status !== "closed" || !item.remark) {
        throw new TaskListError(`TaskItem ${taskItemId} must be closed by a Remark to edit it`);
    }
    const updatedItem: TaskItem = { ...item, remark: { text: remarkText }, updatedAt: at };
    return {
        ...list,
        updatedAt: at,
        items: list.items.map(it => (it.id === taskItemId ? updatedItem : it)),
    };
};

/**
 * Update the content fields of a TaskItem in a drafted list. The item kind
 * cannot be changed — to switch kinds remove the item and add a new one.
 */
export const applyUpdateTaskItem = (list: TaskList, input: UpdateTaskItemInput, at: string): TaskList => {
    if (list.status !== "drafted") {
        throw new TaskListError(
            `Cannot update TaskItems in "${list.status}" status — only "drafted" is allowed`
        );
    }
    const item = list.items.find(it => it.id === input.taskItemId);
    if (!item) {
        throw new TaskListError(`TaskItem ${input.taskItemId} not found in TaskList ${list.id}`);
    }
    if (item.kind !== input.kind) {
        throw new TaskListError(`Cannot change TaskItem kind from "${item.kind}" to "${input.kind}"`);
    }
    let updatedItem: TaskItem;
    if (input.kind === "receipt" && item.kind === "receipt") {
        updatedItem = {
            ...item,
            date: input.date,
            description: input.description,
            amount: input.amount,
            currency: input.currency,
            updatedAt: at,
        };
    } else if (input.kind === "freeform" && item.kind === "freeform") {
        updatedItem = { ...item, name: input.name, description: input.description, updatedAt: at };
    } else {
        throw new TaskListError(`Unexpected kind mismatch`);
    }
    return {
        ...list,
        updatedAt: at,
        items: list.items.map(it => (it.id === input.taskItemId ? updatedItem : it)),
    };
};
